import { getColumnId } from 'components/TableSettings';
import type { WorkOrderActKc2Data, WorkOrderResultViewData } from 'gql/graphql';
import { useSelection } from 'hooks/useSelection';
import { formatActTitle } from 'pages/Project/Common/ActKc2Title/ActKc2Title';
import type { TableColumn } from 'uikit';
import { Badge } from 'uikit';
import { getNumberCell } from 'utils/tableCells';

import { useCallback, useMemo } from 'react';

import { getSeasonAccessor } from '../helpers';

const dataColumnWidth = 121;
const actColumnWidth = 160;

const getActCell = (act: WorkOrderActKc2Data, decimal: number) => ({
    ...getNumberCell({
        title: '',
        accessor: formatActTitle(act.number, act.date),
        width: actColumnWidth,
        align: 'right',
        decimal,
    }),
    title: (
        <div className="actHeader">
            <Badge minified size="s" status="success" />
            <div>{formatActTitle(act.number, act.date)}</div>
        </div>
    ),
});

export const useOrderSummaryTableColumns = (
    instanceId: string,
    data: WorkOrderResultViewData,
    costRounding: number,
): [TableColumn<any>[], (visible: any) => void, (columnId: string) => boolean] => {
    const { seasons = [], actKC2s = [] } = data || {};
    const [selection, , , setSelection] = useSelection(`summary_${instanceId}`);

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const columns = useMemo(
        () => [
            { title: 'Наименование', accessor: 'title', width: 466, disabled: true },
            getNumberCell({
                title: 'План по нз',
                accessor: 'totalPlanCost',
                width: dataColumnWidth,
                align: 'right',
                decimal: costRounding,
            }),
            getNumberCell({
                title: 'Выполнено по нз',
                accessor: 'totalCompletedCost',
                width: dataColumnWidth,
                align: 'right',
                decimal: costRounding,
            }),
            getNumberCell({
                title: 'Остаток по НЗ',
                accessor: 'totalRemainderCost',
                width: dataColumnWidth,
                align: 'right',
                decimal: costRounding,
            }),
            ...seasons.map((season) =>
                getNumberCell({
                    title: `Сезон ${season.year}, план`,
                    accessor: getSeasonAccessor(season.id),
                    width: dataColumnWidth,
                    align: 'right',
                    decimal: costRounding,
                }),
            ),
            ...actKC2s.map((act) => getActCell(act, costRounding)),
        ],
        [actKC2s, costRounding, seasons],
    );

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            const hidden = columns.reduce(
                (acc, column) => {
                    acc[getColumnId(column)] = false;

                    return acc;
                },
                {} as Record<string, false>,
            );

            setSelection({ ...hidden, ...visible });
        },
        [columns, setSelection],
    );

    return [columns, handleTableSettingsConfirm, isColumnVisible];
};
